/**
 * Shared TipTap extension set + custom marks/nodes used by every Word-style
 * editor in Domivus (Vorlagen, Vertragsvorlagen, …).
 *
 * Custom additions on top of the official TipTap extensions:
 *   • FontSize         — mark on TextStyle for arbitrary pt sizes
 *   • ParagraphLayout  — global attrs on paragraph/heading for indent + spacing
 *   • PageBreak        — node that renders a CSS page-break marker
 */
import { Extension, Mark } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";


/* ------------------------------------------------------------------ */
/*  FontSize – stored as inline style "font-size: 12pt"                */
/* ------------------------------------------------------------------ */
export const FontSize = Extension.create({
  name: "fontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => {
              const v = el.style.fontSize;
              if (!v) return null;
              return v.endsWith("pt") ? v : v;
            },
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontSize: size })
            .run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run(),
    };
  },
});


/* ------------------------------------------------------------------ */
/*  ParagraphLayout – indent + spacing on paragraphs & headings        */
/* ------------------------------------------------------------------ */
export const ParagraphLayout = Extension.create({
  name: "paragraphLayout",

  addGlobalAttributes() {
    const styleAttr = (cssKey, suffix = "") => ({
      default: null,
      parseHTML: (el) => {
        const v = el.style[cssKey];
        if (!v) return null;
        const num = parseFloat(v);
        return Number.isFinite(num) ? num : null;
      },
      renderHTML: (attrs, mappedKey = cssKey) => {
        const k = mappedKey || cssKey;
        const val = attrs[Object.keys(attrs).find((x) => x === k)];
        if (val == null) return {};
        return { style: `${camelToKebab(cssKey)}: ${val}${suffix}` };
      },
    });

    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          indentLeft: {
            default: null,
            parseHTML: (el) =>
              el.style.paddingLeft ? parseFloat(el.style.paddingLeft) : null,
            renderHTML: (attrs) =>
              attrs.indentLeft
                ? { style: `padding-left: ${attrs.indentLeft}cm` }
                : {},
          },
          indentRight: {
            default: null,
            parseHTML: (el) =>
              el.style.paddingRight ? parseFloat(el.style.paddingRight) : null,
            renderHTML: (attrs) =>
              attrs.indentRight
                ? { style: `padding-right: ${attrs.indentRight}cm` }
                : {},
          },
          spaceBefore: {
            default: null,
            parseHTML: (el) =>
              el.style.marginTop ? parseFloat(el.style.marginTop) : null,
            renderHTML: (attrs) =>
              attrs.spaceBefore
                ? { style: `margin-top: ${attrs.spaceBefore}pt` }
                : {},
          },
          spaceAfter: {
            default: null,
            parseHTML: (el) =>
              el.style.marginBottom ? parseFloat(el.style.marginBottom) : null,
            renderHTML: (attrs) =>
              attrs.spaceAfter
                ? { style: `margin-bottom: ${attrs.spaceAfter}pt` }
                : {},
          },
          lineHeight: {
            default: null,
            parseHTML: (el) =>
              el.style.lineHeight ? parseFloat(el.style.lineHeight) : null,
            renderHTML: (attrs) =>
              attrs.lineHeight
                ? { style: `line-height: ${attrs.lineHeight}` }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setParagraphAttr:
        (key, value) =>
        ({ chain }) => {
          return chain()
            .updateAttributes("paragraph", { [key]: value })
            .updateAttributes("heading", { [key]: value })
            .run();
        },
    };
  },
});

function camelToKebab(s) {
  return s.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
}


/* ------------------------------------------------------------------ */
/*  AlignedTable – erhält align="center" + width="70%" (zentrierte Kostentabelle) beim Laden/Speichern */
const AlignedTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: { default: null, parseHTML: (el) => el.getAttribute("align"), renderHTML: (a) => (a.align ? { align: a.align } : {}) },
      width: { default: null, parseHTML: (el) => el.getAttribute("width"), renderHTML: (a) => (a.width ? { width: a.width } : {}) },
    };
  },
});

/*  PageBreak – inserted via toolbar, rendered as <hr class="page-break"> */
/* ------------------------------------------------------------------ */
export const PageBreak = Extension.create({
  name: "pageBreak",
  addCommands() {
    return {
      insertPageBreak:
        () =>
        ({ chain }) =>
          chain()
            .insertContent(
              '<hr class="page-break" style="page-break-after: always; border: 0; border-top: 2px dashed #cbd5e1; margin: 1em 0;">'
            )
            .run(),
    };
  },
});


/* ------------------------------------------------------------------ */
/*  Public extension set                                                */
/* ------------------------------------------------------------------ */
// Beim Einfügen fremde Schrift-/Farbangaben entfernen (v. a. Handy-Apps);
// Fett/Kursiv/Listen/Absätze bleiben erhalten.
const STRIP_STYLES = ["font-family", "font-size", "line-height", "letter-spacing",
  "color", "background-color", "background"];
export function cleanPastedHTML(html) {
  if (typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("font").forEach((el) => el.replaceWith(...el.childNodes));
  doc.querySelectorAll("[style]").forEach((el) => {
    STRIP_STYLES.forEach((p) => el.style.removeProperty(p));
    if (!el.getAttribute("style")?.trim()) el.removeAttribute("style");
  });
  doc.querySelectorAll("[face],[size],[color]").forEach((el) => {
    ["face", "size", "color"].forEach((a) => el.removeAttribute(a));
  });
  return doc.body.innerHTML;
}

const PasteCleaner = Extension.create({
  name: "pasteCleaner",
  addProseMirrorPlugins() {
    return [new Plugin({ props: { transformPastedHTML: cleanPastedHTML } })];
  },
});

export function buildEditorExtensions({ placeholder } = {}) {
  return [
    PasteCleaner,
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      // Underline + Link werden separat (mit Custom-Config) registriert,
      // deshalb hier deaktivieren um TipTap "Duplicate extension" Warnings
      // zu vermeiden (StarterKit ≥ 3.x liefert beide standardmäßig mit).
      underline: false,
      link: false,
      horizontalRule: {
        HTMLAttributes: { class: "tiptap-hr" },
      },
    }),
    Underline,
    Subscript,
    Superscript,
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ["heading", "paragraph", "tableCell", "tableHeader"] }),
    ParagraphLayout,
    PageBreak,
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { class: "text-indigo-600 underline" },
    }),
    Image.configure({
      HTMLAttributes: { class: "max-w-full inline-block" },
    }),
    AlignedTable.configure({ resizable: true, HTMLAttributes: { class: "tiptap-table" } }),
    TableRow,
    TableHeader,
    TableCell,
    Placeholder.configure({
      placeholder: placeholder || "Beginne mit dem Schreiben …",
    }),
  ];
}
